'use client';

import { useEffect, useState, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { Eye, ExternalLink } from 'lucide-react';
import { mockApi } from '@/lib/mockApi';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { DashboardShell } from '@/components/layout/DashboardShell';
import { LoadingState } from '@/components/ui/LoadingState';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { OrderStatusBadge } from '@/components/product/OrderStatusBadge';
import type { Order, Product, Shop, OrderStatus } from '@/types';

type PeriodFilter = '7' | '30' | 'all';

export function AdminDashboardPage() {
  const { addToast } = useToast();
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderDialog, setShowOrderDialog] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ordersResponse, productsResponse] = await Promise.all([
        api.orders.getOrders({ limit: 100 }),
        api.products.getProducts({ limit: 100 }),
      ]);
      setOrders(ordersResponse.items);
      setProducts(productsResponse.items);
      // Shops not available via API yet - skip for now
      setShops([]);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err instanceof Error ? err : new Error('Failed to load dashboard data'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter orders by period
  const periodOrders = useMemo(() => {
    if (periodFilter === 'all') return orders;
    const days = parseInt(periodFilter);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    return orders.filter(order => new Date(order.createdAt) >= cutoffDate);
  }, [orders, periodFilter]);

  // Calculate KPIs
  const kpis = useMemo(() => {
    const totalRevenue = periodOrders
      .filter(o => o.status === 'DELIVERED' || o.status === 'PAID' || o.status === 'SHIPPED')
      .reduce((sum, o) => sum + o.amounts.total, 0);
    
    const totalOrders = periodOrders.length;
    
    // New Users - placeholder (no user data in mockApi)
    const newUsers = 0; // TODO: Implement when user management is added
    
    const pendingAttention = periodOrders.filter(
      o => o.status === 'PENDING_PAYMENT' || o.status === 'PAID' || o.status === 'SHIPPED'
    ).length;

    return { totalRevenue, totalOrders, newUsers, pendingAttention };
  }, [periodOrders]);

  // Recent Orders (top 10, newest first)
  const recentOrders = useMemo(() => {
    return [...orders]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 10);
  }, [orders]);

  // Recent Products (top 10, newest first by creation logic - using first 10)
  const recentProducts = useMemo(() => {
    return products.slice(0, 10);
  }, [products]);

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderDialog(true);
  };

  const [seeding, setSeeding] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);

  const handleSeedDemoData = async () => {
    setSeeding(true);
    try {
      const result = await api.admin.generateDemoData({ productsPerShop: 20, ordersPerShop: 10 });
      addToast({ 
        title: 'Амжилттай', 
        description: `Жишээ өгөгдөл үүсгэгдлээ: ${result.created.products} бараа, ${result.created.orders} захиалга` 
      });
      await loadData();
      // Reload page to refresh all data
      window.location.reload();
    } catch (error: any) {
      addToast({ title: 'Алдаа', description: error.message || 'Жишээ өгөгдөл үүсгэхэд алдаа гарлаа', variant: 'destructive' });
    } finally {
      setSeeding(false);
    }
  };

  const handleResetData = async () => {
    setResetting(true);
    try {
      await mockApi.resetAllData();
      addToast({ title: 'Өгөгдөл шинэчлэгдсэн', description: 'Бүх жишээ өгөгдөл шинэчлэгдлээ' });
      setShowResetDialog(false);
      await loadData();
      // Reload page to refresh all data
      window.location.reload();
    } catch (error: any) {
      addToast({ title: 'Алдаа', description: error.message || 'Өгөгдөл шинэчлэхэд алдаа гарлаа', variant: 'destructive' });
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <DashboardShell role="admin" title="Самбар">
        <LoadingState text="Самбар ачааллаж байна..." />
      </DashboardShell>
    );
  }

  if (error) {
    return (
      <DashboardShell role="admin" title="Dashboard">
        <ErrorState
          title="Самбар ачааллахад алдаа гарлаа"
          message="Дахин оролдоно уу."
          onRetry={loadData}
        />
      </DashboardShell>
    );
  }

  return (
    <DashboardShell role="admin" title="Dashboard">
      <div className="space-y-6">
        {/* Period Selector */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Хугацаа:</span>
            <div className="flex gap-2">
              <Button
                variant={periodFilter === '7' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodFilter('7')}
              >
                7 өдөр
              </Button>
              <Button
                variant={periodFilter === '30' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodFilter('30')}
              >
                30 өдөр
              </Button>
              <Button
                variant={periodFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPeriodFilter('all')}
              >
                Бүх хугацаа
              </Button>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Нийт орлого</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(kpis.totalRevenue)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Нийт захиалга</p>
                  <p className="text-2xl font-bold text-gray-900">{kpis.totalOrders}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Шинэ хэрэглэгчид</p>
                  <p className="text-2xl font-bold text-gray-900">{kpis.newUsers}</p>
                  <p className="text-xs text-gray-500 mt-1">TODO: Хэрэглэгчийн удирдлага</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Хүлээгдэж байна / Анхаарал шаардсан</p>
                  <p className="text-2xl font-bold text-gray-900">{kpis.pendingAttention}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Orders */}
          <Card>
            <CardHeader>
              <CardTitle>Сүүлийн захиалга</CardTitle>
            </CardHeader>
            <CardContent>
              {recentOrders.length === 0 ? (
                <EmptyState
                  title="Одоогоор захиалга байхгүй"
                  description="Үйлчлүүлэгчид худалдан авалт хийхэд захиалга энд харагдах болно."
                />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Огноо</TableHead>
                        <TableHead>Үйлчлүүлэгч</TableHead>
                        <TableHead>Нийт</TableHead>
                        <TableHead>Төлөв</TableHead>
                        <TableHead className="text-right">Үйлдэл</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentOrders.map((order) => (
                        <TableRow key={order.id}>
                          <TableCell className="font-mono text-sm">
                            #{order.id.slice(-8)}
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {new Date(order.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <div className="font-medium text-gray-900">
                                {order.address?.fullName || 'Байхгүй'}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="font-semibold">
                            {formatCurrency(order.amounts.total)}
                          </TableCell>
                          <TableCell>
                            <OrderStatusBadge status={order.status} />
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewOrder(order)}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Харах
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Products */}
          <Card>
            <CardHeader>
              <CardTitle>Сүүлийн бараа</CardTitle>
            </CardHeader>
            <CardContent>
              {recentProducts.length === 0 ? (
                <EmptyState
                  title="Одоогоор бараа байхгүй"
                  description="Борлуулагчид бараа нэмэхэд энд харагдах болно."
                />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Нэр</TableHead>
                        <TableHead>Үнэ</TableHead>
                        <TableHead>Төлөв</TableHead>
                        <TableHead className="text-right">Үйлдэл</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentProducts.map((product) => (
                        <TableRow key={product.id}>
                          <TableCell className="font-medium">
                            {product.title}
                          </TableCell>
                          <TableCell>
                            {formatCurrency(product.salePrice || product.price)}
                          </TableCell>
                          <TableCell>
                            {product.isActive ? (
                              <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                                Идэвхтэй
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                                Идэвхгүй
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Link href={`/product/${product.id}`}>
                              <Button variant="ghost" size="sm">
                                <ExternalLink className="h-4 w-4 mr-1" />
                                Нээх
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Demo Data Card */}
        <Card>
          <CardHeader>
            <CardTitle>Жишээ өгөгдөл</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Туршилт болон демо-д зориулсан бодит жишээ өгөгдөл үүсгэнэ. Энэ нь дэлгүүр, бараа, захиалга нэмнэ.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={handleSeedDemoData}
                disabled={seeding}
                className="flex-1"
              >
                {seeding ? 'Үүсгэж байна...' : 'Жишээ өгөгдөл үүсгэх'}
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowResetDialog(true)}
                disabled={resetting}
                className="flex-1"
              >
                {resetting ? 'Шинэчлэж байна...' : 'Бүх өгөгдөл шинэчлэх'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions Panel */}
        <Card>
          <CardHeader>
            <CardTitle>Түргэн үйлдлүүд</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Link href="/admin/orders">
                <Button variant="outline" className="w-full">
                  Захиалга удирдах
                </Button>
              </Link>
              <Link href="/admin/products">
                <Button variant="outline" className="w-full">
                  Бараа удирдах
                </Button>
              </Link>
              <Button
                variant="outline"
                className="w-full"
                disabled
                title="Удахгүй"
              >
                Худалдагч/Дэлгүүр удирдах
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Order Details Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Захиалга #{selectedOrder?.id.slice(-8)}</DialogTitle>
            <DialogDescription>
              Захиалгын дэлгэрэнгүй мэдээлэл
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Бараанууд</h3>
                <div className="space-y-2">
                  {selectedOrder.items.map((item) => (
                    <div key={item.productId} className="flex justify-between text-sm">
                      <span>
                        {item.titleSnapshot} × {item.qty}
                      </span>
                      <span>{formatCurrency(item.priceSnapshot * item.qty)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between font-bold">
                  <span>Нийт:</span>
                  <span>{formatCurrency(selectedOrder.amounts.total)}</span>
                </div>
              </div>
              {selectedOrder.address && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-2">Shipping Address</h3>
                  <p className="text-sm text-gray-600">
                    {selectedOrder.address.fullName}<br />
                    {selectedOrder.address.phone}<br />
                    {selectedOrder.address.city}<br />
                    {selectedOrder.address.addressLine1}
                  </p>
                </div>
              )}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-2">Status History</h3>
                <div className="space-y-2">
                  {selectedOrder.statusHistory.map((entry, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <OrderStatusBadge status={entry.status} />
                      <span className="text-sm text-gray-500">
                        by {entry.byRole} • {new Date(entry.at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Data Confirmation Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Бүх өгөгдөл шинэчлэх</DialogTitle>
            <DialogDescription>
              Та бүх жишээ өгөгдлийг шинэчлэхдээ итгэлтэй байна уу? Энэ нь бүх дэлгүүр, бараа, захиалга болон бусад өгөгдлийг арилгана. Энэ үйлдлийг буцаах боломжгүй.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)} disabled={resetting}>
              Цуцлах
            </Button>
            <Button variant="destructive" onClick={handleResetData} disabled={resetting}>
              {resetting ? 'Шинэчлэж байна...' : 'Бүх өгөгдөл шинэчлэх'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
